import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material'
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { Company } from '@otw/models'
import { useUsers } from 'hooks/useUsers'
import { useCompanies, useCompany } from 'hooks/admin/useCompany'
import { useManageClientFinancingProfiles } from 'hooks/admin/useClientFinancingProfiles'
import { CompanyManagementDialog } from './components/CompanyManagementDialog'
import { AdminPageFrame } from 'components/AdminPageFrame'
import { MobileTabSelect } from 'components/MobileTabSelect'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { StatusChip } from 'components/StatusChip'
import { useIsMobile } from 'hooks/screen/useIsMobile'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`financing-hub-tabpanel-${index}`}
      aria-labelledby={`financing-hub-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

// Helper function to format currency
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const AdminFinancingHub: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { users: allUsers } = useUsers()
  const { companies, isLoading, createCompany, refetch } = useCompanies()

  // Get tab value from URL hash
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '')
    switch (hash) {
      case 'profiles':
        return 0
      case 'companies':
        return 1
      default:
        return 0
    }
  }

  const [tabValue, setTabValue] = useState(getTabFromHash())
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null)
  
  // Hook for deleting a company (only enabled when we have a company to delete)
  const { deleteCompany } = useCompany(companyToDelete?.id || '', {
    enabled: !!companyToDelete?.id,
    dontShowUpdates: false, // Show success/error messages
    onSuccess: () => {
      setDeleteConfirmOpen(false)
      setCompanyToDelete(null)
      refetch() // Refresh the companies list
    },
    onError: (err) => {
      console.error('Failed to delete company:', err)
      // Error message is already shown by the hook
    },
  })

  // Profile filtering and sorting state
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'business_name' | 'profile_completed' | 'last_updated'>(
    'name',
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Sync tab value with URL hash
  useEffect(() => {
    setTabValue(getTabFromHash())
  }, [location.hash])

  // Set initial hash if none exists
  useEffect(() => {
    if (!location.hash) {
      navigate('#profiles', { replace: true })
    }
  }, [])

  // Pagination hook for all client financing profiles (tab 1)
  const {
    financingProfiles: allFinancingProfiles,
    filteredCount,
    totalProfiles,
    isLoading: isLoadingAllProfiles,
    isError: allProfilesError,
    error: allProfilesErrorDetails,
    refetchFinancingProfiles,
    searchTerm: profileSearchTerm,
    setSearchTerm: setProfileSearchTerm,
    profileCompletedFilter,
    setProfileCompletedFilter,
    isProfileCompleted,
  } = useManageClientFinancingProfiles()

  // Filter companies based on search term
  const filteredCompanies = companies.filter(
    company =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.entity_structure.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Calculate stats
  const totalCompanies = companies.length
  const activeCompanies = companies.filter(company => company.is_active).length
  const totalUsers = companies.reduce((sum, company) => sum + company.user_ids.length, 0)
  const avgUsersPerCompany = totalCompanies > 0 ? Math.round((totalUsers / totalCompanies) * 10) / 10 : 0

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    handleHubTabSelect(newValue)
  }

  const handleHubTabSelect = (newValue: number) => {
    const hash = newValue === 0 ? '#profiles' : '#companies'
    navigate(hash)
    setTabValue(newValue)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, company: Company) => {
    event.stopPropagation()
    setSelectedCompany(company)
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedCompany(null)
  }

  const handleCreateCompany = () => {
    setIsCreateDialogOpen(true)
  }

  const handleEditCompany = () => {
    setIsEditDialogOpen(true)
    handleMenuClose()
  }

  const handleDeleteCompany = () => {
    setCompanyToDelete(selectedCompany)
    setDeleteConfirmOpen(true)
    handleMenuClose()
  }

  const confirmDelete = async () => {
    if (companyToDelete?.id) {
      deleteCompany()
    }
  }

  const handleCompanyCreated = (company: Company) => {
    setIsCreateDialogOpen(false)
    refetch() // Refresh the companies list
  }

  const handleCompanyUpdated = (company: Company) => {
    setIsEditDialogOpen(false)
    setSelectedCompany(null)
    refetch() // Refresh the companies list
  }

  const handleCompanyClick = (company: Company) => {
    navigate(`/admin/financing-profiles/company/${company.id}`)
  }

  // Add handler for client profile row clicks
  const handleClientProfileClick = (clientId: string) => {
    navigate(`/admin/financing-profiles/client/${clientId}`)
  }

  const getPrimaryContactName = (userId: string) => {
    const user = allUsers.find(u => u.id === userId)
    return user ? `${user.first_name} ${user.last_name}` : 'Unknown User'
  }

  const formatRevenue = (revenue?: number) => {
    if (!revenue) return 'N/A'
    if (revenue >= 1000000) {
      return `$${(revenue / 1000000).toFixed(1)}M`
    }
    if (revenue >= 1000) {
      return `$${(revenue / 1000).toFixed(0)}K`
    }
    return `$${revenue.toLocaleString()}`
  }

  const handleProfileSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    console.log('Search term changed:', value)
    setProfileSearchTerm(value)
  }

  // Helper function to get last updated date
  const getLastUpdated = (profile: any) => {
    if (!profile.financingProfile) return null

    // Use date_modified field from the financing profile
    if (profile.financingProfile.date_modified) {
      return new Date(profile.financingProfile.date_modified)
    }

    return null
  }

  // Sort profiles
  const sortedProfiles = [...allFinancingProfiles].sort((a, b) => {
    let aValue, bValue

    switch (sortBy) {
      case 'name':
        aValue = `${a.first_name} ${a.last_name}`.toLowerCase()
        bValue = `${b.first_name} ${b.last_name}`.toLowerCase()
        break
      case 'email':
        aValue = (a.email || '').toLowerCase()
        bValue = (b.email || '').toLowerCase()
        break
      case 'business_name':
        aValue = (a.financingProfile?.business_name || '').toLowerCase()
        bValue = (b.financingProfile?.business_name || '').toLowerCase()
        break
      case 'profile_completed':
        aValue = isProfileCompleted(a) ? 1 : 0
        bValue = isProfileCompleted(b) ? 1 : 0
        break
      case 'last_updated':
        aValue = getLastUpdated(a)?.getTime() || 0
        bValue = getLastUpdated(b)?.getTime() || 0
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Handle sort column click
  const handleSortClick = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
  }

  // Format date for display
  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A'
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <AdminPageFrame
      title='Financing Hub'
      action={
        isMobile ? undefined : (
        <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreateCompany}>
          Create Company
        </Button>
        )
      }
    >

      <Paper sx={{ mb: 3, p: { xs: 1.5, sm: 0 } }}>
        <MobileTabSelect
          label='Section'
          ariaLabel='Financing hub sections'
          value={tabValue}
          onChange={handleHubTabSelect}
          tabs={[
            { value: 0, label: 'Profiles' },
            { value: 1, label: 'Companies' },
          ]}
          desktop={
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label='All Client Profiles' />
              <Tab label='Companies' />
            </Tabs>
          }
        />
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* All Client Financing Profiles with Pagination */}
        <Box mb={3}>
          <Typography variant='h6' gutterBottom>
            All Client Financing Profiles
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Browse all client financing profiles with search functionality and filtering.
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Box mb={3}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                placeholder='Search by name, email, or business name...'
                value={profileSearchTerm}
                onChange={handleProfileSearchChange}
                variant='outlined'
                size='small'
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size='small' fullWidth>
                <InputLabel>Profile Status</InputLabel>
                <Select
                  value={profileCompletedFilter}
                  label='Profile Status'
                  onChange={e => setProfileCompletedFilter(e.target.value as 'all' | 'true' | 'false')}
                >
                  <MenuItem value='all'>All Profiles</MenuItem>
                  <MenuItem value='true'>Completed Only</MenuItem>
                  <MenuItem value='false'>Incomplete Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Stats */}
        <Box mb={3}>
          <Typography variant='body2' color='text.secondary'>
            Showing {filteredCount} of {totalProfiles} profiles
          </Typography>
        </Box>

        {/* Error State */}
        {allProfilesError && (
          <Alert severity='error' sx={{ mb: 3 }}>
            Error loading financing profiles: {allProfilesErrorDetails?.message || 'Unknown error'}
            <Button onClick={() => refetchFinancingProfiles()} sx={{ ml: 2 }} size='small'>
              Retry
            </Button>
          </Alert>
        )}

        {/* Loading State */}
        {isLoadingAllProfiles && allFinancingProfiles.length === 0 ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isMobile ? (
              <MobileRecordList
                isEmpty={sortedProfiles.length === 0}
                empty={
                  profileSearchTerm || profileCompletedFilter !== 'all'
                    ? 'No profiles match your search or filters.'
                    : 'No financing profiles have been created yet.'
                }
              >
                {sortedProfiles.map(profile => {
                  const profileCompleted = isProfileCompleted(profile)
                  const lastUpdated = getLastUpdated(profile)
                  return (
                    <MobileRecordCard
                      key={profile.id}
                      onClick={() => handleClientProfileClick(profile.id)}
                      title={`${profile.first_name} ${profile.last_name}`}
                      status={{
                        tone: profileCompleted ? 'success' : 'warning',
                        label: profileCompleted ? 'Complete' : 'Incomplete',
                      }}
                      meta={profile.email || 'No email'}
                      detail={`${profile.financingProfile?.business_name || 'No business name'} · Updated ${formatDate(lastUpdated)}`}
                    />
                  )
                })}
              </MobileRecordList>
            ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortDirection : 'asc'}
                        onClick={() => handleSortClick('name')}
                      >
                        Client Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'email'}
                        direction={sortBy === 'email' ? sortDirection : 'asc'}
                        onClick={() => handleSortClick('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'business_name'}
                        direction={sortBy === 'business_name' ? sortDirection : 'asc'}
                        onClick={() => handleSortClick('business_name')}
                      >
                        Business Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === 'profile_completed'}
                        direction={sortBy === 'profile_completed' ? sortDirection : 'asc'}
                        onClick={() => handleSortClick('profile_completed')}
                      >
                        Profile Completed
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'last_updated'}
                        direction={sortBy === 'last_updated' ? sortDirection : 'asc'}
                        onClick={() => handleSortClick('last_updated')}
                      >
                        Last Updated
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProfiles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box textAlign='center' py={4}>
                          <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant='h6' color='text.secondary'>
                            No financing profiles found
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {profileSearchTerm || profileCompletedFilter !== 'all'
                              ? 'Try adjusting your search terms or filters'
                              : 'No profiles have been created yet'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedProfiles.map(profile => {
                      const profileCompleted = isProfileCompleted(profile)
                      const lastUpdated = getLastUpdated(profile)

                      return (
                        <TableRow
                          key={profile.id}
                          hover
                          onClick={() => handleClientProfileClick(profile.id)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant='body1'>
                              {profile.first_name} {profile.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant='body2'>{profile.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant='body2'
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={profile.financingProfile?.business_name}
                            >
                              {profile.financingProfile?.business_name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align='center'>
                            <Chip
                              label={profileCompleted ? 'Yes' : 'No'}
                              size='small'
                              color={profileCompleted ? 'success' : 'warning'}
                              variant={profileCompleted ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant='body2' color='text.secondary'>
                              {formatDate(lastUpdated)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Search and Create Company */}
        <Box mb={3} display='flex' flexDirection={isMobile ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobile ? 'stretch' : 'center'} gap={2}>
          <TextField
            placeholder='Search companies...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            variant='outlined'
            size='small'
            sx={{ width: isMobile ? '100%' : 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleCreateCompany}
            fullWidth={isMobile}
            sx={{ minHeight: 44, alignSelf: isMobile ? 'stretch' : 'auto' }}
          >
            Create Company
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                  <Box>
                    <Typography variant='h4' color='primary' gutterBottom>
                      {totalCompanies}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Total Companies
                    </Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                  <Box>
                    <Typography variant='h4' color='success.main' gutterBottom>
                      {activeCompanies}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Active Companies
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                  <Box>
                    <Typography variant='h4' color='info.main' gutterBottom>
                      {totalUsers}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Total Users
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                  <Box>
                    <Typography variant='h4' color='warning.main' gutterBottom>
                      {avgUsersPerCompany}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Avg Users/Company
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Companies Grid */}
        <Grid container spacing={3}>
          {filteredCompanies.map(company => {
            const primaryContactName = getPrimaryContactName(company.primary_contact_user_id)

            return (
              <Grid item xs={12} sm={6} md={4} key={company.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => handleCompanyClick(company)}
                >
                  <CardContent>
                    <Box display='flex' justifyContent='space-between' alignItems='flex-start' mb={2}>
                      <Typography variant='h6' component='h3' gutterBottom>
                        {company.name}
                      </Typography>
                      <IconButton size='small' onClick={e => handleMenuClick(e, company)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Chip
                      label={company.entity_structure}
                      size='small'
                      color='primary'
                      variant='outlined'
                      sx={{ mb: 1 }}
                    />

                    {company.industry && (
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        Industry: {company.industry}
                      </Typography>
                    )}

                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      <PeopleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {company.user_ids.length} user{company.user_ids.length !== 1 ? 's' : ''}
                    </Typography>

                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Primary Contact: {primaryContactName}
                    </Typography>

                    {company.annual_revenue && (
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        Annual Revenue: {formatRevenue(company.annual_revenue)}
                      </Typography>
                    )}

                    {company.address && (
                      <Typography variant='body2' color='text.secondary'>
                        {company.address.city}, {company.address.state}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>

        {filteredCompanies.length === 0 && !isLoading && (
          <Box textAlign='center' py={8}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant='h6' color='text.secondary' gutterBottom>
              {searchTerm ? 'No companies found' : 'No companies yet'}
            </Typography>
            <Typography variant='body2' color='text.secondary' mb={3}>
              {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first company'}
            </Typography>
            {!searchTerm && (
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={handleCreateCompany}
                fullWidth={isMobile}
                sx={{ minHeight: 44, maxWidth: isMobile ? '100%' : undefined }}
              >
                Create Company
              </Button>
            )}
          </Box>
        )}
      </TabPanel>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditCompany}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Company
        </MenuItem>
        <MenuItem onClick={handleDeleteCompany} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Company
        </MenuItem>
      </Menu>

      {/* Company Management Dialog - Create */}
      <CompanyManagementDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCompanyCreated={handleCompanyCreated}
        existingCompany={null}
      />

      {/* Company Management Dialog - Edit */}
      <CompanyManagementDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onCompanyCreated={handleCompanyUpdated}
        existingCompany={selectedCompany}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{companyToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color='error' variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageFrame>
  )
}

export default AdminFinancingHub
